import * as React from 'react';

// API
import apibridge from '../../apibridge';

// UTILS
import { validationErrorsToString, WWCCIconDisplay, WWCCStyleDisplay, WWCCTextDisplay } from '../../utils';

// PACKAGES
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { SwitchTransition, CSSTransition } from 'react-transition-group';
import 'moment/locale/en-au';
import { Tooltip } from 'reactstrap';

// COMPONENTS
import Button from '../../components/Button';
import FormErrorMessage from '../../components/ErrorMessage';
import LoadingSpinner from '../../components/LoadingSpinner';
import DisplayDateExpiry from '../../components/DisplayDateExpiry';
import DisplayDate from '../../components/DisplayDate';
import DisplayDateTime from '../../components/DisplayDateTime';
import StatusIcon from '../../components/StatusIcon';

// ICONS
import { ReactComponent as DownloadIcon } from '../../assets/svg/download.svg';
import { ReactComponent as InfoIcon } from '../../assets/svg/Information.svg';

// TYPES
import {
	DomainCertificationType,
	RequestsToJoinFetchCertification,
	RequestsToJoinFetchProfile,
	RequestsToJoinReviewCertificationCertificationReview,
	WorkingWithChildrenCheckSingleStatusCheckCommand,
	WorkingWithChildrenCheckSingleStatusCheckStatusCheckResult
} from '../../api/models';
import { closeModalType } from '../../components/GlobalModal';
import { RequiredValidationCertificationsStatus } from './RequestsToJoinDetail';

interface DetailModalTypes {
	profileData: RequestsToJoinFetchProfile;
	modalDetail: RequestsToJoinFetchCertification;
	closeModal?: closeModalType;
	requiredValidationCertificationsStatus: RequiredValidationCertificationsStatus[] | undefined;
	setRequiredValidationCertificationsStatus: React.Dispatch<
		React.SetStateAction<RequiredValidationCertificationsStatus[] | undefined>
	>;
}

const DetailModal: React.FC<DetailModalTypes> = ({
	profileData,
	modalDetail,
	closeModal,
	requiredValidationCertificationsStatus,
	setRequiredValidationCertificationsStatus
}) => {
	const [showError, setShowError] = React.useState<boolean>(false);

	const [isLoading, setIsLoading] = React.useState<boolean>(false);

	const [formErrorMessage, setFormErrorMessage] = React.useState<string>();

	const {
		certificationTypeDisplayName,
		certificationType,
		state,
		certificationNumber,
		userAcknowledgement,
		expiry,
		date,
		id,
		attachments
	} = modalDetail;

	// Set up State to get all Certification Detail Data
	const initialCertificationDetail: RequestsToJoinReviewCertificationCertificationReview = {
		id: undefined,
		certificationType: undefined,
		reviewType: undefined,
		displayName: undefined,
		date: undefined,
		expiry: undefined,
		state: undefined,
		certificationNumber: undefined,
		confirmed: undefined,
		organisation: undefined,
		title: undefined,
		cited: undefined,
		attachments: []
	};

	const [certificationDetail, setCertificationDetail] =
		React.useState<RequestsToJoinReviewCertificationCertificationReview>(initialCertificationDetail);

	const getCertificationDetail = async () => {
		setIsLoading(true);

		const response = await apibridge.getCertificationDetail(id || '', profileData.id || '');

		if (response.isError) {
			setIsLoading(false);

			setFormErrorMessage(
				response.validationErrors.length
					? validationErrorsToString(response.validationErrors)
					: response.message || 'An error has occurred'
			);
		} else {
			const { result = initialCertificationDetail } = response;

			setIsLoading(false);

			setCertificationDetail(result);
		}
	};

	React.useEffect(() => {
		getCertificationDetail();
	}, []);

	// Find the Id of the registration
	const registrationStatus = requiredValidationCertificationsStatus?.find((item) => item.id === modalDetail.id);

	const registrationId = registrationStatus?.id;

	// Check if the certification is confirmed
	const isCertificationsConfirmed = registrationStatus?.isRegistrationConfirmed;

	// Submit Logic
	const submitHandler = async (values: any) => {
		setIsLoading(true);

		if (certificationType === DomainCertificationType.TeacherRegistration) {
			requiredValidationCertificationsStatus?.forEach((item) => {
				if (item.id === registrationId) {
					item.isRegistrationConfirmed = values.teacherRegistrationConfirmInput;
				}
			});

			setRequiredValidationCertificationsStatus(requiredValidationCertificationsStatus);
		}

		if (certificationType === DomainCertificationType.WorkingWithChildrenCheck) {
			requiredValidationCertificationsStatus?.forEach((item) => {
				if (item.id === registrationId) {
					item.isRegistrationConfirmed = values.childrenCheckConfirmInput;
				}
			});

			setRequiredValidationCertificationsStatus(requiredValidationCertificationsStatus);
		}

		if (closeModal) {
			closeModal();
		}
	};

	const validationSchema = Yup.object({
		// isCitedInput: Yup.boolean().required('Required')
	});

	const initialValues = {
		certificationType: certificationDetail.certificationType,
		teacherRegistrationConfirmInput: isCertificationsConfirmed,
		childrenCheckConfirmInput: isCertificationsConfirmed
	};

	// ===== WWCC API Logic =====
	const [adminTooltipOpen, setAdminTooltipOpen] = React.useState<boolean>(false);
	const toggleAdminTooltip = () => setAdminTooltipOpen(!adminTooltipOpen);

	// Initial State for WWCC Status
	const initialWWCCStatus: WorkingWithChildrenCheckSingleStatusCheckStatusCheckResult = {
		isSuccess: false,
		eligibleToWork: certificationDetail.wwccEligibleToWork,
		message: certificationDetail.wwccMessage || '',
		expiryDate: '',
		checkDate: certificationDetail.wwccLastChecked || ''
	};

	// Main State for WWCC Status
	const [WWCCStatus, setWWCCStatus] =
		React.useState<WorkingWithChildrenCheckSingleStatusCheckStatusCheckResult>(initialWWCCStatus);

	// State to manage WWCC Status functions
	const [isLoadingWWCCStatusCheck, setIsLoadingWWCCStatusCheck] = React.useState<boolean>(false);
	const [showWWCCStatusError, setShowWWCCStatusError] = React.useState<boolean>(false);

	// Set WWCC Status when certificationDetail changes (but preserve expiry)
	React.useEffect(() => {
		setWWCCStatus((prev) => ({
			...prev,
			eligibleToWork: certificationDetail.wwccEligibleToWork,
			message: certificationDetail.wwccMessage || '',
			checkDate: certificationDetail.wwccLastChecked || '',
			// Don't override expiryDate from API response
			expiryDate: prev.expiryDate
		}));
	}, [certificationDetail.wwccEligibleToWork, certificationDetail.wwccMessage, certificationDetail.wwccLastChecked]);

	// Function to check WWCC Status
	const confirmCheckWWWCStatus = async () => {
		setIsLoadingWWCCStatusCheck(true);
		setShowWWCCStatusError(false);

		const submitData: WorkingWithChildrenCheckSingleStatusCheckCommand = {
			certificationReviewId: certificationDetail.id || '',
			state: certificationDetail.state || '',
			referenceNumber: modalDetail.certificationNumber || '',
			surname: profileData.lastName
		};

		const response = await apibridge.postWorkingwithchildrencheckSinglestatuscheck(submitData);

		if (response.isError) {
			setShowWWCCStatusError(true);
		} else {
			// Type assertion after checking isError is false
			const successResponse = response as { result?: WorkingWithChildrenCheckSingleStatusCheckStatusCheckResult };
			if (successResponse.result) {
				const result = successResponse.result;
				const newExpiryDate = result.expiryDate;

				if (newExpiryDate) {
					setCertificationDetail((prevState) => ({
						...prevState,
						expiry: newExpiryDate
					}));
				}

				setWWCCStatus(result);
			}
		}

		setIsLoadingWWCCStatusCheck(false);
	};

	return (
		<Formik
			initialValues={initialValues}
			validationSchema={validationSchema}
			onSubmit={submitHandler}
			enableReinitialize
		>
			{(formik) => {
				return (
					<Form translate="yes" className="general-form mtop-0">
						<>
							{showError ? (
								<FormErrorMessage message={formErrorMessage} />
							) : (
								<SwitchTransition mode="out-in">
									<CSSTransition
										key={`key_${isLoading}`}
										timeout={600}
										classNames="standalone-fade"
										mountOnEnter
										unmountOnExit
									>
										{isLoading ? (
											<LoadingSpinner show={isLoading} />
										) : (
											<>
												<h3 className="document-title">{certificationTypeDisplayName}</h3>
												<section className="general-document-detail row">
													{certificationType && (
														<>
															<div className="col-4 document-name">Type</div>

															<div className="col-8 document-detail">{certificationTypeDisplayName}</div>
														</>
													)}

													{certificationDetail.organisation && (
														<>
															<div className="col-4 document-name">Organisation</div>

															<div className="col-8 document-detail">{certificationDetail.organisation}</div>
														</>
													)}

													{certificationDetail.title && (
														<>
															<div className="col-4 document-name">Title</div>

															<div className="col-8 document-detail">{certificationDetail.title}</div>
														</>
													)}

													{state && (
														<>
															<div className="col-4 document-name">State</div>

															<div className="col-8 document-detail">{state}</div>
														</>
													)}

													{certificationNumber && (
														<>
															<div className="col-4 document-name">Number</div>

															<div className="col-8 document-detail">{certificationNumber}</div>
														</>
													)}

													{expiry && (
														<>
															<div className="col-4 document-name">Expiry</div>

															<div className="col-8 document-detail">
																<DisplayDateExpiry>{expiry}</DisplayDateExpiry>
															</div>
														</>
													)}

													{date && (
														<>
															<div className="col-4 document-name">Date</div>

															<div className="col-8 document-detail">
																<DisplayDate>{date}</DisplayDate>
															</div>
														</>
													)}

													{userAcknowledgement &&
														certificationType !== DomainCertificationType.VaccinationCertificate && (
															<>
																<div className="col-4 document-name">Notified Authority</div>

																<div className="col-8 document-detail">
																	<Field
																		readOnly
																		type="checkbox"
																		checked={userAcknowledgement}
																		name="userAcknowledgement"
																	/>
																</div>
															</>
														)}

													{/* WWCC API Logic */}
													{certificationDetail.wwccCanPerformSingleStatusCheck ? (
														<>
															<div className="col-12">
																<div className="general-divider"></div>
															</div>

															<div className="col-4 mb-2">
																<div className="d-flex align-items-center fw-medium text-grey-4">Status</div>
															</div>

															<div className="col-8 mb-2">
																<div className="d-flex gap-2 align-items-center justify-content-start">
																	<StatusIcon
																		statusType={WWCCIconDisplay(WWCCStatus.eligibleToWork)}
																		width={24}
																		height={24}
																	/>

																	<p className={`mb-0 fw-medium ${WWCCStyleDisplay(WWCCStatus.eligibleToWork)}`}>
																		Eligible to work: {WWCCTextDisplay(WWCCStatus.eligibleToWork)}
																	</p>

																	{WWCCStatus.message ? (
																		<>
																			<InfoIcon className="info-icon mt-0" id="childaware-admin-description-tooltip" />

																			<Tooltip
																				placement="top"
																				isOpen={adminTooltipOpen}
																				autohide={false}
																				target="childaware-admin-description-tooltip"
																				toggle={toggleAdminTooltip}
																			>
																				<div className="tooltip-content">{WWCCStatus.message}</div>
																			</Tooltip>
																		</>
																	) : null}
																</div>
															</div>

															<div className="col-4">
																<div className="d-flex align-items-center h-100 fw-medium text-grey-4">Last check</div>
															</div>

															<div className="col-4 pr-0">
																<div className="d-flex align-items-center h-100">
																	<span className={`fw-medium ${WWCCStyleDisplay(WWCCStatus.eligibleToWork)}`}>
																		{WWCCStatus.checkDate ? (
																			<DisplayDateTime format="DD/MM/YYYY, hh:mma">
																				{WWCCStatus.checkDate}
																			</DisplayDateTime>
																		) : (
																			<>Not checked yet</>
																		)}
																	</span>
																</div>
															</div>

															<div className="col-4">
																<div className="check-status-wrapper">
																	{isLoadingWWCCStatusCheck ? (
																		<div className="d-flex align-items-center gap-2">
																			<span className="text-grey-5 font-weight-bold">Processing...</span>

																			<LoadingSpinner show={true} isStatic width={18} height={18} />
																		</div>
																	) : (
																		<button className="btn-secondary btn-check-status" onClick={confirmCheckWWWCStatus}>
																			Check Status
																		</button>
																	)}
																</div>
															</div>

															{showWWCCStatusError && (
																<div className="col-12">
																	<p className="body-small fw-medium text-alert-warning mt-2 mb-0 text-right">
																		Something went wrong.
																	</p>
																	<p className="body-small fw-medium text-alert-warning mb-0 text-right">
																		Please try again in a few minutes
																	</p>
																</div>
															)}

															<div className="col-12">
																<div className="general-divider mb-0"></div>
															</div>
														</>
													) : null}

													{/* User Acknowledgement */}
													{userAcknowledgement &&
														certificationType === DomainCertificationType.VaccinationCertificate && (
															<>
																<div className="col-4 document-name">User acknowledged</div>

																<div className="col-8 document-detail">
																	<Field
																		readOnly
																		type="checkbox"
																		checked={userAcknowledgement}
																		name="userAcknowledgement"
																	/>
																</div>
															</>
														)}
												</section>

												{attachments && attachments.length > 0 && (
													<div className="form-group">
														<label htmlFor="additionalCommentInput" className="label">
															Attachments
														</label>

														<ul>
															{attachments.map((item, index) => {
																return (
																	<li key={index}>
																		{item && item.attachmentUrl && (
																			<a className="attachments-download-link" href={item.attachmentUrl} download>
																				<span className="link-name">{item.displayFileName}</span>

																				<span className="link-text d-none d-lg-block">Download</span>

																				<DownloadIcon className="link-icon d-block d-lg-none" />
																			</a>
																		)}
																	</li>
																);
															})}
														</ul>
													</div>
												)}

												{/* IMPORTANT NOTE: CLIENT SPECIFICALLY ASKED NOT TO DELETE THESE "SIGHTED FUNCTIONALITY" AND
												ONLY COMMENT THEM OUT */}

												{/* <div className="form-group">
													<label className="form-control-checkbox">
														<Field type="checkbox" name="isCitedInput" />

														<span>I have physically sighted this document</span>
													</label>

													<ErrorMessage name="isCitedInput" component="div" className="form-validation-error" />
												</div> */}

												{certificationType === DomainCertificationType.TeacherRegistration && (
													<div className="form-group">
														<label className="form-control-checkbox">
															<Field type="checkbox" name="teacherRegistrationConfirmInput" />

															<span>
																I have checked with the teacher registration authority and confirmed that this person
																has a valid and current teacher registration.
															</span>
														</label>

														<ErrorMessage
															name="teacherRegistrationConfirmInput"
															component="div"
															className="form-validation-error"
														/>
													</div>
												)}

												{certificationType === DomainCertificationType.WorkingWithChildrenCheck && (
													<div className="form-group">
														<label className="form-control-checkbox">
															<Field type="checkbox" name="childrenCheckConfirmInput" />

															<span>
																I have checked with the Working With Children Check authority and confirmed that this
																person has a valid and current WWCC and is authorised to work or volunteer with
																children.
															</span>
														</label>

														<ErrorMessage
															name="childrenCheckConfirmInput"
															component="div"
															className="form-validation-error"
														/>
													</div>
												)}

												{(certificationType === DomainCertificationType.WorkingWithChildrenCheck ||
													certificationType === DomainCertificationType.TeacherRegistration) && (
													<div className="form-button-group">
														<Button type="submit" className="btn-primary w-100" disabled={isLoadingWWCCStatusCheck}>
															Save Change
														</Button>
													</div>
												)}

												<Button
													className="btn-secondary w-100 mtop-16"
													onClick={() => {
														if (closeModal) {
															closeModal();
														}
													}}
													disabled={isLoadingWWCCStatusCheck}
												>
													Cancel
												</Button>
											</>
										)}
									</CSSTransition>
								</SwitchTransition>
							)}
						</>
					</Form>
				);
			}}
		</Formik>
	);
};

export default DetailModal;
